import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Check, X, Eye, DollarSign, Edit, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '../components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  useContributions, 
  useMembers, 
  useCreateContribution, 
  useConfirmContribution, 
  useDeleteContribution, 
  useUpdateContribution 
} from '../hooks/useQueries';
import type { Contribution } from '../services';
import { Thai } from '../utils/thai';
import { toast } from 'sonner';
import { ErrorCard } from '../components/error';
import { SkeletonTable, SkeletonDashboardCard } from '../components/ui/skeleton';
import { MemberSelect } from '../components/forms';

const contributionFormSchema = z.object({
  memberId: z.string().min(1, 'Member is required'),
  amount: z.number().min(1, 'Amount must be greater than 0'),
  year: z.number().min(2023, 'Invalid year'),
  month: z.number().min(1).max(12, 'Invalid month'),
  paymentDate: z.string().min(1, 'Payment date is required'),
  paymentMethod: z.enum(['cash', 'transfer']),
  receiptImageUrl: z.string().optional(),
  notes: z.string().optional(),
});

type ContributionFormData = z.infer<typeof contributionFormSchema>;

export function ContributionsPage() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedContribution, setSelectedContribution] = useState<Contribution | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [contributionToConfirm, setContributionToConfirm] = useState<Contribution | null>(null);
  const [contributionToDelete, setContributionToDelete] = useState<Contribution | null>(null);
  const [contributionToEdit, setContributionToEdit] = useState<Contribution | null>(null);

  // Fetch contributions
  const { data, isLoading, error } = useContributions({ 
    year: selectedYear, 
    month: selectedMonth
  });

  // Fetch members for the dropdown
  const { data: membersData } = useMembers();

  // Contribution mutations
  const createContributionMutation = useCreateContribution();
  const confirmContributionMutation = useConfirmContribution();
  const deleteContributionMutation = useDeleteContribution();
  const updateContributionMutation = useUpdateContribution();

  // Create form
  const createForm = useForm<ContributionFormData>({
    resolver: zodResolver(contributionFormSchema),
    defaultValues: {
      memberId: '',
      amount: 500, // Default monthly contribution
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'cash',
      receiptImageUrl: '',
      notes: '',
    },
  });

  // Edit form
  const editForm = useForm<ContributionFormData>({
    resolver: zodResolver(contributionFormSchema),
    defaultValues: {
      memberId: '',
      amount: 500,
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'cash',
      receiptImageUrl: '',
      notes: '',
    },
  });

  // Handle success states
  useEffect(() => {
    if (createContributionMutation.isSuccess) {
      setIsCreateDialogOpen(false);
      toast.success(t('contributions.createSuccess'));
      createForm.reset();
    }
  }, [createContributionMutation.isSuccess, createForm, t]);

  useEffect(() => {
    if (confirmContributionMutation.isSuccess) {
      setIsConfirmDialogOpen(false);
      setContributionToConfirm(null);
      toast.success(t('contributions.confirmSuccess'));
    }
  }, [confirmContributionMutation.isSuccess, t]);

  useEffect(() => {
    if (deleteContributionMutation.isSuccess) {
      setIsDeleteDialogOpen(false);
      setContributionToDelete(null);
      toast.success(t('contributions.deleteSuccess'));
    }
  }, [deleteContributionMutation.isSuccess, t]);

  useEffect(() => {
    if (updateContributionMutation.isSuccess) {
      setIsEditDialogOpen(false);
      setContributionToEdit(null);
      toast.success(t('contributions.updateSuccess'));
      editForm.reset();
    }
  }, [updateContributionMutation.isSuccess, editForm, t]);

  // Handle error states
  useEffect(() => {
    if (createContributionMutation.isError) {
      toast.error(t('contributions.createError'));
    }
  }, [createContributionMutation.isError, t]);

  useEffect(() => {
    if (confirmContributionMutation.isError) {
      toast.error(t('contributions.confirmError'));
    }
  }, [confirmContributionMutation.isError, t]);

  useEffect(() => {
    if (deleteContributionMutation.isError) {
      toast.error(t('contributions.deleteError'));
    }
  }, [deleteContributionMutation.isError, t]);

  useEffect(() => {
    if (updateContributionMutation.isError) {
      toast.error(t('contributions.updateError'));
    }
  }, [updateContributionMutation.isError, t]);

  const handleCreateSubmit = (data: ContributionFormData) => {
    // Convert date string to ISO datetime for the backend
    const submissionData = {
      ...data,
      paymentDate: new Date(data.paymentDate).toISOString()
    };
    createContributionMutation.mutate(submissionData);
  };

  const handleEditSubmit = (data: ContributionFormData) => {
    if (!contributionToEdit) return;
    
    // Convert date string to ISO datetime for the backend
    const submissionData = {
      ...data,
      paymentDate: new Date(data.paymentDate).toISOString()
    };
    updateContributionMutation.mutate({ 
      id: contributionToEdit.contributionId, 
      data: submissionData 
    });
  };

  const handleView = (contribution: Contribution) => {
    setSelectedContribution(contribution);
    setIsViewDialogOpen(true);
  };

  const handleEdit = (contribution: Contribution) => {
    setContributionToEdit(contribution);
    // Populate the edit form with existing data
    editForm.reset({
      memberId: contribution.memberId,
      amount: contribution.amount,
      year: contribution.year,
      month: contribution.month,
      paymentDate: new Date(contribution.paymentDate).toISOString().split('T')[0],
      paymentMethod: contribution.paymentMethod as 'cash' | 'transfer',
      receiptImageUrl: contribution.receiptImageUrl || '',
      notes: contribution.notes || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (contribution: Contribution) => {
    setContributionToDelete(contribution);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteSubmit = () => {
    if (contributionToDelete) {
      deleteContributionMutation.mutate(contributionToDelete.contributionId);
    }
  };

  const handleConfirmClick = (contribution: Contribution) => {
    setContributionToConfirm(contribution);
    setIsConfirmDialogOpen(true);
  };

  const handleConfirmSubmit = () => {
    if (contributionToConfirm) {
      confirmContributionMutation.mutate(contributionToConfirm.contributionId);
    }
  };

  const getPaymentMethodDisplay = (method: string) => {
    switch (method) {
      case 'cash':
        return t('contributions.paymentMethods.cash');
      case 'transfer':
        return t('contributions.paymentMethods.transfer');
      default:
        return method;
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'pending':
        return t('contributions.status.pending');
      case 'confirmed':
        return t('contributions.status.confirmed');
      default:
        return status;
    }
  };

  const canConfirmContribution = () => {
    // const userRole = user?.publicMetadata?.role as string;

    // return userRole === 'treasurer' || userRole === 'board';

    return true;
  };

  // Generate month options
  const monthOptions = Thai.Date.getMonthNames().map((name, index) => ({
    value: index + 1,
    label: name,
  }));

  // Generate year options (current year and previous 2 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2].map(year => ({
    value: year,
    label: `${year} (${Thai.Date.toBuddhistYear(year)})`,
  }));

  if (error) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">{t('contributions.title')}</h1>
          <p className="text-gray-600">{t('contributions.subtitle')}</p>
        </div>
        <ErrorCard error={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  // Filter contributions based on search
  const filteredContributions = data?.items.filter(contribution => {
    if (!searchQuery) return true;
    const member = membersData?.items.find(m => m.memberId === contribution.memberId);
    return member?.name.toLowerCase().includes(searchQuery.toLowerCase());
  }) || [];

  // Calculate summary statistics
  const totalAmount = filteredContributions.reduce((sum, c) => sum + c.amount, 0) || 0;
  const confirmedAmount = filteredContributions.filter(c => c.status === 'confirmed').reduce((sum, c) => sum + c.amount, 0) || 0;
  const pendingAmount = filteredContributions.filter(c => c.status === 'pending').reduce((sum, c) => sum + c.amount, 0) || 0;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">{t('contributions.title')}</h1>
        <p className="text-gray-600">{t('contributions.subtitle')}</p>
      </div>

      {/* Summary Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <SkeletonDashboardCard />
          <SkeletonDashboardCard />
          <SkeletonDashboardCard />
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('contributions.summary.total')}</p>
              <p className="text-2xl font-semibold">{Thai.Currency.format(totalAmount)}</p>
            </div>
            <DollarSign className="h-8 w-8 text-gray-400" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('contributions.summary.confirmed')}</p>
              <p className="text-2xl font-semibold text-green-600">{Thai.Currency.format(confirmedAmount)}</p>
            </div>
            <Check className="h-8 w-8 text-green-400" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('contributions.summary.pending')}</p>
              <p className="text-2xl font-semibold text-yellow-600">{Thai.Currency.format(pendingAmount)}</p>
            </div>
            <X className="h-8 w-8 text-yellow-400" />
          </div>
        </Card>
      </div>
      )}

      <Card className="p-6">
        <div className="mb-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="search"
                placeholder={t('contributions.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="rounded-md border border-gray-300 px-3 py-2"
            >
              {monthOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="rounded-md border border-gray-300 px-3 py-2"
            >
              {yearOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <Button onClick={() => setIsCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              {t('contributions.addContribution')}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <SkeletonTable rows={5} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('contributions.table.member')}</TableHead>
                <TableHead>{t('contributions.table.amount')}</TableHead>
                <TableHead>{t('contributions.table.paymentDate')}</TableHead>
                <TableHead>{t('contributions.table.paymentMethod')}</TableHead>
                <TableHead>{t('contributions.table.status')}</TableHead>
                <TableHead className="text-right">{t('contributions.table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredContributions.map((contribution) => {
                const member = membersData?.items.find(m => m.memberId === contribution.memberId);
                return (
                  <TableRow key={contribution.contributionId}>
                    <TableCell>{member?.name || contribution.memberId}</TableCell>
                    <TableCell>{Thai.Currency.format(contribution.amount)}</TableCell>
                    <TableCell>{new Date(contribution.paymentDate).toLocaleDateString('th-TH')}</TableCell>
                    <TableCell>{getPaymentMethodDisplay(contribution.paymentMethod)}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        contribution.status === 'confirmed' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {getStatusDisplay(contribution.status)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => handleView(contribution)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {contribution.status === 'pending' && canConfirmContribution() && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(contribution)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleConfirmClick(contribution)}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(contribution)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('contributions.addContribution')}</DialogTitle>
          </DialogHeader>
          <Form {...createForm}>
            <form onSubmit={createForm.handleSubmit(handleCreateSubmit)} className="space-y-4">
              <FormField
                control={createForm.control}
                name="memberId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contributions.form.member')}</FormLabel>
                    <FormControl>
                      <MemberSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder={t('contributions.form.selectMember')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={createForm.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contributions.form.amount')}</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        {...field} 
                        onChange={(e) => field.onChange(parseFloat(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={createForm.control}
                  name="month"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('contributions.form.month')}</FormLabel>
                      <FormControl>
                        <select {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} className="w-full rounded-md border border-gray-300 px-3 py-2">
                          {monthOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={createForm.control}
                  name="year"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('contributions.form.year')}</FormLabel>
                      <FormControl>
                        <select {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} className="w-full rounded-md border border-gray-300 px-3 py-2">
                          {yearOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={createForm.control}
                name="paymentDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contributions.form.paymentDate')}</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={createForm.control}
                name="paymentMethod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contributions.form.paymentMethod')}</FormLabel>
                    <FormControl>
                      <select {...field} className="w-full rounded-md border border-gray-300 px-3 py-2">
                        <option value="cash">{t('contributions.paymentMethods.cash')}</option>
                        <option value="transfer">{t('contributions.paymentMethods.transfer')}</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={createForm.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contributions.form.notes')}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t('contributions.form.notesPlaceholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={createContributionMutation.isPending}>
                  {createContributionMutation.isPending ? t('common.saving') : t('common.save')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('contributions.viewContribution')}</DialogTitle>
          </DialogHeader>
          {selectedContribution && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">{t('contributions.form.member')}</label>
                <p className="mt-1">
                  {membersData?.items.find(m => m.memberId === selectedContribution.memberId)?.name || selectedContribution.memberId}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('contributions.form.amount')}</label>
                <p className="mt-1">{Thai.Currency.format(selectedContribution.amount)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('contributions.form.period')}</label>
                <p className="mt-1">
                  {monthOptions[selectedContribution.month - 1]?.label} {selectedContribution.year} ({Thai.Date.toBuddhistYear(selectedContribution.year)})
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('contributions.form.paymentDate')}</label>
                <p className="mt-1">{new Date(selectedContribution.paymentDate).toLocaleDateString('th-TH')}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('contributions.form.paymentMethod')}</label>
                <p className="mt-1">{getPaymentMethodDisplay(selectedContribution.paymentMethod)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('contributions.form.status')}</label>
                <p className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedContribution.status === 'confirmed' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {getStatusDisplay(selectedContribution.status)}
                  </span>
                </p>
              </div>
              {selectedContribution.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500">{t('contributions.form.notes')}</label>
                  <p className="mt-1">{selectedContribution.notes}</p>
                </div>
              )}
              {selectedContribution.confirmedBy && (
                <div>
                  <label className="text-sm font-medium text-gray-500">{t('contributions.form.confirmedBy')}</label>
                  <p className="mt-1">{selectedContribution.confirmedBy}</p>
                </div>
              )}
              {selectedContribution.confirmedAt && (
                <div>
                  <label className="text-sm font-medium text-gray-500">{t('contributions.form.confirmedAt')}</label>
                  <p className="mt-1">{new Date(selectedContribution.confirmedAt).toLocaleString('th-TH')}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsViewDialogOpen(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('contributions.confirmTitle')}</DialogTitle>
          </DialogHeader>
          {contributionToConfirm && (
            <div className="space-y-4">
              <p className="text-gray-600">
                {t('contributions.confirmMessage', { 
                  name: membersData?.items.find(m => m.memberId === contributionToConfirm.memberId)?.name,
                  amount: Thai.Currency.format(contributionToConfirm.amount)
                })}
              </p>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('contributions.form.period')}</span>
                  <span className="text-sm font-medium">
                    {monthOptions[contributionToConfirm.month - 1]?.label} {contributionToConfirm.year}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('contributions.form.paymentMethod')}</span>
                  <span className="text-sm font-medium">
                    {getPaymentMethodDisplay(contributionToConfirm.paymentMethod)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('contributions.form.paymentDate')}</span>
                  <span className="text-sm font-medium">
                    {new Date(contributionToConfirm.paymentDate).toLocaleDateString('th-TH')}
                  </span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConfirmDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleConfirmSubmit}
              disabled={confirmContributionMutation.isPending}
            >
              {confirmContributionMutation.isPending ? t('common.confirming') : t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('contributions.editContribution')}</DialogTitle>
          </DialogHeader>
          <Form {...editForm}>
            <form onSubmit={editForm.handleSubmit(handleEditSubmit)} className="space-y-4">
              <FormField
                control={editForm.control}
                name="memberId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contributions.form.member')}</FormLabel>
                    <FormControl>
                      <MemberSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder={t('contributions.form.selectMember')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contributions.form.amount')}</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        {...field} 
                        onChange={(e) => field.onChange(parseFloat(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={editForm.control}
                  name="month"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('contributions.form.month')}</FormLabel>
                      <FormControl>
                        <select {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} className="w-full rounded-md border border-gray-300 px-3 py-2">
                          {monthOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="year"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('contributions.form.year')}</FormLabel>
                      <FormControl>
                        <select {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} className="w-full rounded-md border border-gray-300 px-3 py-2">
                          {yearOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={editForm.control}
                name="paymentDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contributions.form.paymentDate')}</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="paymentMethod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contributions.form.paymentMethod')}</FormLabel>
                    <FormControl>
                      <select {...field} className="w-full rounded-md border border-gray-300 px-3 py-2">
                        <option value="cash">{t('contributions.paymentMethods.cash')}</option>
                        <option value="transfer">{t('contributions.paymentMethods.transfer')}</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contributions.form.notes')}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t('contributions.form.notesPlaceholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={updateContributionMutation.isPending}>
                  {updateContributionMutation.isPending ? t('common.saving') : t('common.save')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('contributions.deleteTitle')}</DialogTitle>
          </DialogHeader>
          {contributionToDelete && (
            <div className="space-y-4">
              <p className="text-gray-600">
                {t('contributions.deleteMessage', { 
                  name: membersData?.items.find(m => m.memberId === contributionToDelete.memberId)?.name,
                  amount: Thai.Currency.format(contributionToDelete.amount)
                })}
              </p>
              <div className="bg-red-50 p-4 rounded-lg space-y-2 border border-red-200">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('contributions.form.member')}</span>
                  <span className="text-sm font-medium">
                    {membersData?.items.find(m => m.memberId === contributionToDelete.memberId)?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('contributions.form.amount')}</span>
                  <span className="text-sm font-medium">
                    {Thai.Currency.format(contributionToDelete.amount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">{t('contributions.form.period')}</span>
                  <span className="text-sm font-medium">
                    {monthOptions[contributionToDelete.month - 1]?.label} {contributionToDelete.year}
                  </span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteSubmit}
              disabled={deleteContributionMutation.isPending}
            >
              {deleteContributionMutation.isPending ? t('common.deleting') : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}